import React, { useEffect } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { CheckCircle, Close as CloseIcon } from '@mui/icons-material';
import { steps } from './constants';
import StepPrestation from './steps/StepPrestation';
import StepTarification from './steps/StepTarification';
import StepDisponibilite from './steps/StepDisponibilite';
import StepHoraires from './steps/StepHoraires';

const OffreFormDialog = ({
  open,
  onClose,
  activeStep,
  setActiveStep,
  loading,
  isEditMode,
  formData,
  setFormData,
  categories,
  intervenants,
  user,
  today,
  isStepValid,
  onSubmit,
  onToggleJourOff,
}) => {
  // Réinitialiser à la première étape lorsque la modale se ferme
  useEffect(() => {
    if (!open) {
      setActiveStep(0);
    }
  }, [open, setActiveStep]);

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose()}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          height: '90vh',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'row',
          overflow: 'hidden',
        },
      }}
    >
      {/* SIDEBAR STEPPER */}
      <Box
        sx={{
          width: '300px',
          bgcolor: '#F9FBFF',
          p: 4,
          borderRight: '1px solid #E2EBF6',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
        }}
      >
        <Typography variant="h6" fontWeight="800" color="#1B2559" mb={4}>
          Configuration
        </Typography>
        <Stepper
          activeStep={activeStep}
          orientation="vertical"
          sx={{ '& .MuiStepConnector-line': { minHeight: '30px' } }}
        >
          {steps.map((s, i) => (
            <Step key={i}>
              <StepLabel
                StepIconComponent={() => (
                  <Avatar
                    sx={{
                      width: 42,
                      height: 42,
                      bgcolor:
                        activeStep === i ? '#4318FF' : activeStep > i ? '#05CD99' : '#fff',
                      color: activeStep >= i ? '#fff' : '#A3AED0',
                      border: activeStep < i ? '2px solid #E2EBF6' : 'none',
                      boxShadow:
                        activeStep === i ? '0px 8px 16px rgba(67, 24, 255, 0.25)' : 'none',
                    }}
                  >
                    {activeStep > i ? <CheckCircle sx={{ fontSize: 22 }} /> : s.icon}
                  </Avatar>
                )}
              >
                <Box ml={1}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="800"
                    color={activeStep >= i ? '#1B2559' : '#A3AED0'}
                  >
                    {s.label}
                  </Typography>
                  <Typography variant="caption" color="#A3AED0">
                    {s.desc}
                  </Typography>
                </Box>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* MAIN CONTENT */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#fff', px: { xs: 2, md: 4 }, py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <IconButton onClick={onClose} sx={{ bgcolor: '#F4F7FE' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 1 }}>
          <Typography variant="h5" fontWeight="800" color="#1B2559" mb={1}>
            {steps[activeStep]?.label}
          </Typography>
          <Typography variant="body2" color="#A3AED0" mb={4}>
            Veuillez vérifier et configurer les détails de votre offre de consultation.
          </Typography>

          {activeStep === 0 && (
            <StepPrestation
              formData={formData}
              setFormData={setFormData}
              categories={categories}
              intervenants={intervenants}
              user={user}
            />
          )}
          {activeStep === 1 && (
            <StepTarification formData={formData} setFormData={setFormData} />
          )}
          {activeStep === 2 && (
            <StepDisponibilite formData={formData} setFormData={setFormData} today={today} />
          )}
          {activeStep === 3 && (
            <StepHoraires formData={formData} setFormData={setFormData} onToggleJourOff={onToggleJourOff} />
          )}
        </Box>

        <DialogActions
          sx={{ p: 3, borderTop: '1px solid #E2EBF6', bgcolor: '#fff', justifyContent: 'space-between' }}
        >
          <Button
            onClick={activeStep === 0 ? onClose : () => setActiveStep(activeStep - 1)}
            sx={{ color: '#718096', fontWeight: '800', textTransform: 'none' }}
          >
            {activeStep === 0 ? 'Annuler' : 'Revenir en arrière'}
          </Button>
          <Button
            variant="contained"
            disabled={!isStepValid() || loading}
            onClick={activeStep === steps.length - 1 ? onSubmit : () => setActiveStep(activeStep + 1)}
            sx={{
              bgcolor: '#4318FF',
              borderRadius: '16px',
              px: 6,
              py: 1.5,
              fontWeight: '800',
              textTransform: 'none',
              boxShadow: '0px 10px 20px rgba(67, 24, 255, 0.2)',
            }}
          >
            {loading
              ? 'Traitement...'
              : activeStep === steps.length - 1
                ? isEditMode
                  ? 'Mettre à jour'
                  : 'Publier maintenant'
                : 'Étape suivante'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default OffreFormDialog;